<template>
  <div class="w-full">
    <label :for="question.id" class="block ml-1 text-sm font-semibold leading-6 text-gray-800">
      {{ question.title }}
    </label>

    <div class="relative mt-2 w-full rounded-md shadow-sm">
      <input
        type="number"
        :id="question.id"
        :name="question.id"
        v-model.number="model"
        :step="isFloat ? '0.1' : '1'"
        :disabled="disabled"
        @blur="v$.model.$touch"
        class="block px-3 py-3.5 pr-10 w-full text-gray-700 rounded-md border-0 ring-1 ring-inset ring-gray-300 focus:ring-theme-400 focus:ring-inset focus-visible:outline-none sm:text-sm sm:leading-6"
        :class="{
          'border-red-500 ring-red-500': v$.model.$error && v$.model.$dirty,
        }"
        :aria-invalid="v$.model.$error" />
    </div>
    <p v-if="v$.model.$error && v$.model.$dirty" class="ml-1 text-xs text-red-600">
      {{ firstError }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { helpers, required, integer, minValue, maxValue } from '@vuelidate/validators';
import type { QstQuestionDetails } from '@/types/Questionnaires';
import { useI18n } from 'vue-i18n';
import { MWDataTypes } from '@/utils/constants';

const props = withDefaults(
  defineProps<{
    question: QstQuestionDetails;
    disabled?: boolean;
  }>(),
  { disabled: false }
);

const model = defineModel<number | null>();

const typeName = props.question.mwDataType?.name ?? '';
const isFloat = computed(() => typeName.startsWith(MWDataTypes.FLOAT));
const maskRegex = isValidRegex(props.question.mwDataType?.mask);

const { t: $t } = useI18n({ useScope: 'global' });

function isValidRegex(pattern?: string | null): RegExp | null {
  if (!pattern?.trim()) return null;
  try {
    return new RegExp(`^${pattern}$`);
  } catch {
    return null;
  }
}

const rules = computed(() => {
  const question = props.question;
  const record: Record<string, any> = {};

  if (!question.allowNull) {
    record.required = helpers.withMessage($t('messages.errors.requiredField'), required);
  }

  if (maskRegex) {
    record.regexMask = helpers.withMessage(
      $t('messages.errors.invalidValue'),
      (v: unknown) => v == null || maskRegex.test(String(v))
    );
  } else {
    if (isFloat.value) {
      record.number = helpers.withMessage(
        $t('messages.errors.number'),
        (v: unknown) => v == null || Number.isFinite(+v)
      );
    } else {
      record.integer = helpers.withMessage($t('messages.errors.integer'), integer);
    }

    switch (typeName) {
      case MWDataTypes.INTN:
      case MWDataTypes.FLOATN:
        record.positive = helpers.withMessage(
          $t('messages.errors.greaterThan0'),
          (v: unknown) => v == null || +v > 0
        );
        break;
      case MWDataTypes.INTN0:
      case MWDataTypes.FLOATN0:
        record.nonNegative = helpers.withMessage($t('messages.errors.nonNegative'), minValue(0));
        break;
    }
  }

  if (question.lalo !== undefined && question.lalo !== null) {
    record.min = helpers.withMessage(
      `${$t('messages.errors.minValue')} ${question.lalo}`,
      minValue(+question.lalo)
    );
  }
  if (question.halo !== undefined && question.halo !== null) {
    record.max = helpers.withMessage(
      `${$t('messages.errors.maxValue')} ${question.halo}`,
      maxValue(+question.halo)
    );
  }

  return { model: record };
});

const v$ = useVuelidate(rules, { model }, { $lazy: true });

const firstError = computed(() => {
  const errs = v$.value.model.$errors;
  return errs.length ? errs[0].$message : $t('messages.errors.invalidValue');
});
</script>
